import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { gamesAPI, tickerAPI, gameplayerAPI, playertoolsAPI, matchplayersAPI, matchMetaAPI, projectsAPI, roundsAPI, teamtselAPI } from '../../services/api';
import { useTickerSocket } from '../../hooks/useTickerSocket';
import { calculateMinute, clockAction } from '../../utils/matchClock';
import { toStr, matchPlayerToRosterEntry } from '../../utils/tickerLineupUtils';
import TickerHeader from './TickerHeader';
import TickerTab from './TickerTab';
import LineupTab from './LineupTab';
import WidgetEmbed from './WidgetEmbed';
import ReportGenerator from './ReportGenerator';
import './ticker.css';

export default function GameTickerPage({ standalone = false }) {
    const { projectId, roundId, gameId } = useParams();

    const [game, setGame] = useState(null);
    const [project, setProject] = useState(null);
    const [round, setRound] = useState(null);
    const [events, setEvents] = useState([]);
    const [lineup, setLineup] = useState([]);
    const [availablePlayers, setAvailablePlayers] = useState([]);
    const [trackedTeamId, setTrackedTeamId] = useState('');
    const [meta, setMeta] = useState(null);
    const [displayMinute, setDisplayMinute] = useState(0);
    const [secondaryView, setSecondaryView] = useState(null);
    const [squadPanelOpen, setSquadPanelOpen] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const metaRef = useRef(null);

    useEffect(() => {
        metaRef.current = meta;
        setDisplayMinute(calculateMinute(meta));
    }, [meta]);

    useEffect(() => {
        if (meta?.status === 'live') setSquadPanelOpen(false);
        if (meta?.status === 'prepared') setSquadPanelOpen(true);
    }, [meta?.status]);

    useEffect(() => {
        const interval = setInterval(() => {
            const m = metaRef.current;
            if (m?.clockRunning && !m?.clockPaused) {
                setDisplayMinute(calculateMinute(m));
            }
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    useTickerSocket(gameId, {
        onEvent: useCallback((data) => {
            setEvents((prev) => {
                if (prev.find((e) => e._id === data._id)) return prev;
                return [...prev, data];
            });
        }, []),
        onUpdate: useCallback((data) => {
            setEvents((prev) => prev.map((e) => (e._id === data._id ? data : e)));
        }, []),
        onDelete: useCallback(({ id }) => {
            setEvents((prev) => prev.filter((e) => e._id !== id));
        }, []),
        onScore: useCallback((data) => {
            setMeta((prev) => (prev ? { ...prev, scoreFT: data.scoreFT, scoreHT: data.scoreHT } : prev));
        }, []),
        onStatus: useCallback((data) => {
            setMeta((prev) => (prev ? { ...prev, status: data.status } : prev));
        }, []),
    });

    useEffect(() => {
        loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameId]);

    const resolveTrackedTeamId = async (pid, hid, aid, projectData) => {
        let tracked = toStr(projectData?.trackedTeamId?._id || projectData?.trackedTeamId);
        if (tracked) return tracked;

        try {
            const tselRes = await teamtselAPI.getByProject(pid);
            const first = tselRes.data?.[0];
            tracked = toStr(first?.tid?._id || first?.tid);
            if (tracked) return tracked;
        } catch (_) { /* optional */ }

        const [homeRes, awayRes] = await Promise.all([
            playertoolsAPI.getAll({ pid, tid: hid }),
            playertoolsAPI.getAll({ pid, tid: aid }),
        ]);
        const homeCount = (homeRes.data?.playertools || []).length;
        const awayCount = (awayRes.data?.playertools || []).length;
        return homeCount >= awayCount ? toStr(hid) : toStr(aid);
    };

    const loadRosterPlayers = async (g, projectData) => {
        const pid = g.pid?._id || g.pid;
        const hid = g.hid?._id || g.hid;
        const aid = g.aid?._id || g.aid;
        const tracked = await resolveTrackedTeamId(pid, hid, aid, projectData);
        const opponentId = tracked === toStr(hid) ? toStr(aid) : toStr(hid);

        const [ownRes, matchRes] = await Promise.all([
            playertoolsAPI.getAll({ pid, tid: tracked }),
            matchplayersAPI.getAll({ pid, tid: opponentId }),
        ]);

        setTrackedTeamId(tracked);
        setAvailablePlayers([
            ...(ownRes.data?.playertools || []).map((pt) => ({ ...pt, source: 'playertool' })),
            ...(matchRes.data?.matchplayers || []).map((mp) => matchPlayerToRosterEntry(mp, opponentId)),
        ]);
    };

    const handleMatchPlayersChange = async (teamId, matchPlayers) => {
        setAvailablePlayers((prev) => {
            const withoutTeam = prev.filter((entry) => toStr(entry.tid) !== toStr(teamId));
            const fresh = (matchPlayers || []).map((mp) => matchPlayerToRosterEntry(mp, teamId));
            return [...withoutTeam, ...fresh];
        });
    };

    const loadAll = async () => {
        setLoading(true);
        setError('');
        try {
            const [gameRes, tickerRes, lineupRes, metaRes] = await Promise.all([
                gamesAPI.getById(gameId),
                tickerAPI.getByGame(gameId),
                gameplayerAPI.getByGame(gameId),
                matchMetaAPI.get(gameId),
            ]);

            const g = gameRes.data;
            setGame(g);
            setEvents(tickerRes.data);
            setLineup(lineupRes.data || []);
            setMeta(metaRes.data);

            let projectData = null;
            if (projectId) {
                try {
                    const projectRes = await projectsAPI.getById(projectId);
                    projectData = projectRes.data;
                    setProject(projectData);
                } catch (_) {}
            }
            if (roundId) {
                roundsAPI.getById(roundId).then((r) => setRound(r.data)).catch(() => {});
            }

            if (g?.pid && g?.hid && g?.aid) {
                await loadRosterPlayers(g, projectData);
            }
        } catch (err) {
            setError('Fehler beim Laden der Spieldaten');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreated = (newEvent) => {
        setEvents((prev) => {
            if (prev.find((e) => e._id === newEvent._id)) return prev;
            return [...prev, newEvent];
        });
    };

    const handleDelete = (id) => setEvents((prev) => prev.filter((e) => e._id !== id));
    const handleUpdate = (updated) => setEvents((prev) => prev.map((e) => (e._id === updated._id ? updated : e)));

    const handleClockAction = async (action) => {
        const payload = clockAction(action, meta || {});
        if (!payload) return;
        try {
            const res = await matchMetaAPI.update(gameId, payload);
            setMeta(res.data);

            const autoTexts = {
                kickoff: { type: 9, text: 'Anpfiff!' },
                halftime: { type: 6, text: `Halbzeit – Stand: ${meta?.scoreFT?.home ?? 0}:${meta?.scoreFT?.away ?? 0}` },
                second_half: { type: 9, text: 'Wiederanpfiff – 2. Halbzeit' },
                fulltime: { type: 7, text: `Abpfiff – Endstand: ${meta?.scoreFT?.home ?? 0}:${meta?.scoreFT?.away ?? 0}` },
            };
            if (autoTexts[action]) {
                const { type, text } = autoTexts[action];
                await tickerAPI.create({
                    gid: gameId,
                    type,
                    minute: action === 'halftime' ? 45 : action === 'fulltime' ? 90 : displayMinute,
                    half: action === 'second_half' ? 2 : meta?.currentHalf || 1,
                    textRaw: text,
                    textPublished: text,
                    published: true,
                });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const toggleSquadPanel = useCallback(() => {
        setSquadPanelOpen((v) => !v);
    }, []);

    const workspaceClass = `ticker-workspace ${standalone ? 'ticker-workspace--standalone' : ''}`;

    if (loading) {
        return (
            <div className={`${workspaceClass} flex items-center justify-center`}>
                <div className="text-center">
                    <span className="loading loading-spinner loading-lg text-green-500" />
                    <p className="mt-3 text-slate-500">Lade Spieldaten…</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`${workspaceClass} p-6`}>
                <div className="ticker-modal max-w-md mx-auto">
                    <p className="text-red-400 mb-3">⚠️ {error}</p>
                    <button type="button" className="ticker-clock-btn ticker-clock-btn--go" onClick={loadAll}>Nochmal versuchen</button>
                </div>
            </div>
        );
    }

    return (
        <div className={workspaceClass}>
            <TickerHeader
                game={game}
                project={project}
                round={round}
                meta={meta}
                displayMinute={displayMinute}
                onClockAction={handleClockAction}
                onMetaUpdate={setMeta}
                onOpenMore={setSecondaryView}
            />

            {secondaryView && (
                <div className="ticker-secondary-overlay">
                    <div className="ticker-secondary-overlay-head">
                        <h2>
                            {secondaryView === 'lineup' && 'Aufstellung bearbeiten'}
                            {secondaryView === 'report' && 'Spielbericht'}
                            {secondaryView === 'embed' && 'Widget'}
                        </h2>
                        <button type="button" className="ticker-clock-btn" onClick={() => setSecondaryView(null)}>← Zurück zum Ticker</button>
                    </div>
                    <div className="ticker-secondary-panel">
                        {secondaryView === 'lineup' && (
                            <LineupTab
                                game={game}
                                gameId={gameId}
                                lineup={lineup}
                                availablePlayers={availablePlayers}
                                onLineupChange={setLineup}
                                trackedTeamId={trackedTeamId}
                                onMatchPlayersChange={handleMatchPlayersChange}
                            />
                        )}
                        {secondaryView === 'report' && (
                            <ReportGenerator gameId={gameId} events={events} />
                        )}
                        {secondaryView === 'embed' && (
                            <WidgetEmbed gameId={gameId} />
                        )}
                    </div>
                </div>
            )}

            {!secondaryView && (
                <div className="ticker-main ticker-main--wide">
                    <TickerTab
                        gameId={gameId}
                        game={game}
                        events={events}
                        lineup={lineup}
                        availablePlayers={availablePlayers}
                        meta={meta}
                        displayMinute={displayMinute}
                        onCreated={handleCreated}
                        onDelete={handleDelete}
                        onUpdate={handleUpdate}
                        onMetaUpdate={setMeta}
                        onLineupChange={setLineup}
                        squadPanelOpen={squadPanelOpen}
                        onToggleSquadPanel={toggleSquadPanel}
                    />
                </div>
            )}
        </div>
    );
}
